import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import autoTable, {Styles} from 'jspdf-autotable';
import moment, {Moment} from 'moment';
import {translations} from '../../../../i18n/translations';
import {getOccurrenceStatus} from '../../occurrences';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {EntityOccurrence} from '../../../../backend/responses/entity-occurrence';
import {OccurrenceStatus} from '../../../../screens/private/office/professional-caregiver/occurrences/occurrences.professional-caregiver.screen';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {formatPdfDateRangeTitle, pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {PDF_FONT_MAIN_COLOR} from '../base/export-pdf-font';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedPdfExportFile, PreparedXlsxExportFile} from '../base/export-data-types';
import {
  PDF_STATUS_COLOR_DONE,
  PDF_STATUS_COLOR_MISSED,
  PDF_STATUS_COLOR_PLANNED,
} from '../base/export-pdf-status-colors';

const STATUS_COLOR: Record<OccurrenceStatus, string> = {
  handled: PDF_STATUS_COLOR_DONE,
  not_handled: PDF_STATUS_COLOR_MISSED,
  registered: PDF_STATUS_COLOR_PLANNED,
};

export function prepareOccurrencesPDF({
  reportData,
  startDate,
  endDate,
  t,
  entity,
}: {
  reportData: any[];
  startDate: Moment;
  endDate: Moment;
  t: TFunction;
  entity?: EntityInfo;
}): PreparedPdfExportFile {
  const reportTitle = t(translations.screen_paths.institution_settings_occurrences);
  const reportDateRange = formatPdfDateRangeTitle({startDate, endDate});
  const pdf = new jsPDF({orientation: 'landscape', compress: true});
  pdf.setProperties({
    title: `${reportTitle}: ${reportDateRange}`,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  const tableStartY = pdfAddBodyTitle(pdf, {
    title: reportTitle,
    subtitle: reportDateRange,
  });

  let table: any[] = [];

  for (let occurrence of reportData) {
    const senior = occurrence.targetUserId;
    const responsible = occurrence.responsibleUserId;
    const registeredBy = occurrence.registeredByUserId;
    const statusType = getOccurrenceStatus(occurrence as EntityOccurrence);
    const occurrenceDate = moment(occurrence.registeredAt);

    const date = occurrenceDate.format('DD/MM/YYYY HH:mm');
    const type = occurrence.typeId.name;
    const seniorName = senior?.displayName ?? senior?.name ?? '';
    const registeredByName = registeredBy?.displayName ?? registeredBy?.name ?? '';
    const responsibleName = responsible?.displayName ?? responsible?.name ?? '';

    const cell = (cellContent: string | null | undefined, styles?: Partial<Styles>) => ({
      content: cellContent ?? '',
      styles: styles,
    });

    const statusLabel = t(
      translations.office.professional_caregiver.occurrences.status[statusType]
    );
    const siteName = occurrence.siteName ?? '';
    const details = occurrence.details ?? '';
    const notes = occurrence.notes ?? '';

    // `cell` helper is defined above to allow passing `styles` for PDF cells

    table.push([
      cell(date),
      cell(type),
      cell(seniorName),
      cell(siteName),
      cell(details),
      cell(registeredByName),
      cell(responsibleName),
      cell(statusLabel, {textColor: STATUS_COLOR[statusType]}),
      cell(notes),
    ]);
  }

  // Translations
  const dateStr = t(translations.office.professional_caregiver.occurrences.table_header_date);
  const typeStr = t(translations.office.professional_caregiver.occurrences.table_header_type);
  const seniorStr = t(translations.office.professional_caregiver.occurrences.table_header_patient);
  const responsibleStr = t(
    translations.office.professional_caregiver.occurrences.table_header_responsible
  );
  const registeredByStr = t(
    translations.office.professional_caregiver.occurrences.table_header_registered
  );
  const statusStr = t(translations.office.professional_caregiver.occurrences.table_header_status);
  const siteNameStr = t(translations.daily_care_log.occurrences.local);
  const detailsStr = t(translations.daily_care_log.occurrences.details);
  const notesStr = t(translations.care_and_activities.notes);

  autoTable(pdf, {
    body: table,
    head: [
      [
        dateStr,
        typeStr,
        seniorStr,
        siteNameStr,
        detailsStr,
        registeredByStr,
        responsibleStr,
        statusStr,
        notesStr,
      ],
    ],
    startY: tableStartY,
    styles: {fontSize: 8},
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    columnStyles: {
      0: {cellWidth: 25}, // Date
      4: {cellWidth: 50}, // Details
      8: {cellWidth: 50}, // Notes
    },
    willDrawPage: function (data) {
      // ---- Add Header to first page ----
      if (data.pageNumber === 1) {
        pdfHeaderInfo(pdf, undefined, entity, t);

        // Reset custom margins for the rest of the pages
        data.settings.margin.top = 7;
        data.settings.margin.bottom = 15;
      }
    },
    didDrawPage: function (data) {
      // ---- Add Footer to all pages ----
      const pageCounter = data.pageNumber.toString();
      pdfFooterInfo(pdf, pageCounter);
    },
  });

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.office.professional_caregiver.settings.generic_header.breadcrumbs.occurrences),
  ]);

  return {
    file: pdf,
    fileName,
  };
}

export function prepareOccurrencesXLSX({
  reportData,
  entity,
  t,
}: {
  reportData: any[];
  entity?: EntityInfo;
  t: TFunction;
}): PreparedXlsxExportFile {
  let rows: string[][] = [];

  for (let occurrence of reportData) {
    const senior = occurrence.targetUserId;
    const responsible = occurrence.responsibleUserId;
    const registeredBy = occurrence.registeredByUserId;
    const statusType = getOccurrenceStatus(occurrence as EntityOccurrence);
    const occurrenceDate = moment(occurrence.registeredAt);

    const date = occurrenceDate.format('DD/MM/YYYY HH:mm');
    const type = occurrence.typeId.name;
    const seniorName = senior?.displayName ?? senior?.name ?? '';
    const responsibleName = responsible?.displayName ?? responsible?.name ?? '';
    const registeredByName = registeredBy?.displayName ?? registeredBy?.name ?? '';
    const status = t(translations.office.professional_caregiver.occurrences.status[statusType]);
    const siteName = occurrence.siteName ?? '';
    const details = occurrence.details ?? '';
    const notes = occurrence.notes ?? '';

    rows.push([
      date,
      type,
      seniorName,
      siteName,
      details,
      registeredByName,
      responsibleName,
      status,
      notes,
    ]);
  }

  // Translations
  const dateStr = t(translations.office.professional_caregiver.occurrences.table_header_date);
  const typeStr = t(translations.office.professional_caregiver.occurrences.table_header_type);
  const seniorStr = t(translations.office.professional_caregiver.occurrences.table_header_patient);
  const responsibleStr = t(
    translations.office.professional_caregiver.occurrences.table_header_responsible
  );
  const registeredByStr = t(
    translations.office.professional_caregiver.occurrences.table_header_registered
  );
  const statusStr = t(translations.office.professional_caregiver.occurrences.table_header_status);
  const siteNameStr = t(translations.daily_care_log.occurrences.local);
  const detailsStr = t(translations.daily_care_log.occurrences.details);
  const notesStr = t(translations.care_and_activities.notes);

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  // Create a new worksheet
  let worksheet = XLSX.utils.json_to_sheet(rows);
  // Add headers to worksheet
  worksheet = XLSX.utils.sheet_add_aoa(worksheet, [
    [
      dateStr,
      typeStr,
      seniorStr,
      siteNameStr,
      detailsStr,
      registeredByStr,
      responsibleStr,
      statusStr,
      notesStr,
    ],
  ]);
  // Set columns width
  worksheet['!cols'] = [
    {wch: 20}, // Date
    {wch: 25}, // Type
    {wch: 20}, // Senior
    {wch: 20}, // Site Name
    {wch: 40}, // Details
    {wch: 20}, // Registered By
    {wch: 20}, // Responsible
    {wch: 15}, // Status
    {wch: 40}, // Notes
  ];
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet);

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.office.professional_caregiver.settings.generic_header.breadcrumbs.occurrences),
  ]);

  return {
    file: workbook,
    fileName,
  };
}
